    import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
  IconButton,
} from '@mui/material';
import {
  LocationOn as LocationIcon,
  Close as CloseIcon,
  OpenInNew as OpenInNewIcon,
} from '@mui/icons-material';
import { configurationService } from '../../services/configurationService';

// Declaração de tipos para Google Maps
declare global {
  interface Window {
    google: {
      maps: {
        Map: any;
        Marker: any;
        Size: any;
        Point: any;
        MapMouseEvent: any;
      };
    };
  }
}

interface MapSelectorModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (lat: number, lng: number) => void;
  initialCoordinates?: { lat: number; lng: number } | null;
  title?: string;
  description?: string;
}

const MapSelectorModal: React.FC<MapSelectorModalProps> = ({
  open,
  onClose,
  onConfirm,
  initialCoordinates,
  title = "📍 Selecionar Localização no Mapa",
  description = "Clique no mapa para selecionar a localização"
}) => {
  const [selectedLocation, setSelectedLocation] = useState<{lat: number, lng: number} | null>(
    initialCoordinates || null
  );
  const [loadingLocation, setLoadingLocation] = useState(false);

  // Atualizar localização selecionada quando as coordenadas iniciais mudarem
  useEffect(() => {
    if (initialCoordinates) {
      setSelectedLocation(initialCoordinates);
    } else {
      // Coordenadas padrão (centro do Brasil)
      setSelectedLocation({ lat: -14.235004, lng: -51.92528 });
    }
  }, [initialCoordinates]);

  const handleConfirmLocation = () => {
    if (selectedLocation) {
      onConfirm(selectedLocation.lat, selectedLocation.lng);
      onClose();
    }
  };

  const handleMapClick = (lat: number, lng: number) => {
    setSelectedLocation({ lat, lng });
  };

  const handleClose = () => {
    // Reset para coordenadas iniciais ao fechar sem confirmar
    setSelectedLocation(initialCoordinates || { lat: -14.235004, lng: -51.92528 });
    setLoadingLocation(false);
    onClose();
  };

  const handleOpenGoogleMaps = () => {
    if (!selectedLocation) return;
    window.open(
      `https://www.google.com/maps?q=${selectedLocation.lat},${selectedLocation.lng}`,
      '_blank',
      'noopener,noreferrer'
    );
  };

  const handleGetCurrentLocation = () => {
    if (!navigator.geolocation) {
      alert('Geolocalização não é suportada por este navegador.');
      return;
    }

    setLoadingLocation(true);

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        const newLocation = { lat: latitude, lng: longitude };
        setSelectedLocation(newLocation);
        setLoadingLocation(false);
      },
      (error) => {
        console.error('Erro ao obter localização:', error);
        let errorMessage = 'Erro ao obter localização.';
        
        switch (error.code) {
          case error.PERMISSION_DENIED:
            errorMessage = 'Acesso à localização foi negado. Permita o acesso à localização nas configurações do navegador.';
            break;
          case error.POSITION_UNAVAILABLE:
            errorMessage = 'Informações de localização não estão disponíveis.';
            break;
          case error.TIMEOUT:
            errorMessage = 'Tempo limite para obter localização foi excedido.';
            break;
        }
        
        alert(errorMessage);
        setLoadingLocation(false);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 60000
      }
    );
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">
            {title}
          </Typography>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      
      <DialogContent>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary">
            {description}
          </Typography>
          
          <Button
            variant="outlined"
            size="small"
            startIcon={loadingLocation ? <CircularProgress size={16} /> : <LocationIcon />}
            onClick={handleGetCurrentLocation}
            disabled={loadingLocation}
            sx={{ 
              minWidth: 'auto',
              whiteSpace: 'nowrap',
              ml: 2
            }}
          >
            {loadingLocation ? 'Obtendo...' : 'Minha Localização'}
          </Button>
        </Box>
        
        {selectedLocation && (
          <Alert
            severity="info"
            sx={{ mb: 2 }}
            action={
              <Button
                color="inherit"
                size="small"
                startIcon={<OpenInNewIcon />}
                onClick={handleOpenGoogleMaps}
              >
                Abrir no Google Maps
              </Button>
            }
          >
            <strong>Coordenadas selecionadas:</strong><br />
            Latitude: {selectedLocation.lat.toFixed(6)}<br />
            Longitude: {selectedLocation.lng.toFixed(6)}
          </Alert>
        )}
        
        <MapSelector
          center={selectedLocation || { lat: -14.235004, lng: -51.92528 }}
          selectedLocation={selectedLocation}
          onLocationSelect={handleMapClick}
        />
      </DialogContent>
      
      <DialogActions>
        <Button onClick={handleClose}>
          Cancelar
        </Button>
        <Button
          variant="contained"
          onClick={handleConfirmLocation}
          disabled={!selectedLocation}
          startIcon={<LocationIcon />}
        >
          Confirmar Localização
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Componente interno para o mapa
interface MapSelectorProps {
  center: { lat: number; lng: number };
  selectedLocation: { lat: number; lng: number } | null;
  onLocationSelect: (lat: number, lng: number) => void;
}

const MapSelector: React.FC<MapSelectorProps> = ({ center, selectedLocation, onLocationSelect }) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [mapReady, setMapReady] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const mapRef = useRef<any>(null);
  const markerRef = useRef<any>(null);

  useEffect(() => {
    // Verificar se Google Maps já está carregado
    if (window.google && window.google.maps) {
      setIsLoaded(true);
      return;
    }

    // Carregar Google Maps API
    const loadGoogleMaps = async () => {
      try {
        // Buscar API Key das configurações (mesmo padrão do CaixaMapComponent)
        const config = await configurationService.getConfigurationByKey('google_maps_api_key');
        
        if (!config || !config.valor || !config.ativo) {
          throw new Error('API Key do Google Maps não configurada ou inativa');
        }

        const apiKey = config.valor;

        // Carregar script do Google Maps
        const script = document.createElement('script');
        script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places`;
        script.async = true;
        script.defer = true;
        script.onload = () => setIsLoaded(true);
        script.onerror = () => setError('Erro ao carregar Google Maps');
        document.head.appendChild(script);

      } catch (err: any) {
        setError(err.message || 'Erro ao configurar mapa');
      }
    };

    loadGoogleMaps();
  }, []);

  // UseEffect para criar o mapa apenas uma vez
  useEffect(() => {
    if (!isLoaded || !window.google || mapRef.current) return;

    const mapElement = document.getElementById('map-selector-reusable');
    if (!mapElement) return;

    try {
      // Criar mapa apenas uma vez
      mapRef.current = new window.google.maps.Map(mapElement, {
        center: center,
        zoom: 15,
        mapTypeId: 'roadmap',
        streetViewControl: true,
        mapTypeControl: true,
        fullscreenControl: true,
        zoomControl: true,
      });

      // Listener para cliques no mapa (só criar uma vez)
      mapRef.current.addListener('click', (event: any) => {
        const lat = event.latLng.lat();
        const lng = event.latLng.lng();
        
        onLocationSelect(lat, lng);
      });

      setMapReady(true);
    } catch (err) {
      setError('Erro ao inicializar o mapa');
    }
  }, [isLoaded, onLocationSelect]);

  useEffect(() => {
    if (!mapRef.current || !center) return;
    mapRef.current.panTo(center);
  }, [center]);

  // UseEffect separado para gerenciar apenas o marcador de seleção
  useEffect(() => {
    if (!mapRef.current || !window.google || !mapReady) return;

    if (markerRef.current) {
      markerRef.current.setMap(null);
      markerRef.current = null;
    }

    if (selectedLocation) {
      markerRef.current = new window.google.maps.Marker({
        position: selectedLocation,
        map: mapRef.current,
        title: 'Localização Selecionada',
        icon: {
          url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="#1976d2">
              <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
            </svg>
          `),
          scaledSize: new window.google.maps.Size(30, 30),
          anchor: new window.google.maps.Point(15, 30)
        }
      });
    }
  }, [selectedLocation, mapReady]);

  if (error) {
    return (
      <Alert severity="error">
        {error}
      </Alert>
    );
  }

  if (!isLoaded) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
        <CircularProgress />
        <Typography variant="body2" sx={{ ml: 2 }}>
          Carregando mapa...
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      id="map-selector-reusable"
      sx={{
        width: '100%',
        height: 400,
        borderRadius: 1,
        border: '1px solid #ddd'
      }}
    />
  );
};

export default MapSelectorModal; 